import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { ApplicationActions } from '@galo-monitor/stores/application'
import DataTable from '@galo-monitor/web-components/DataTable'
import Screen from '@galo-monitor/web-components/Screen'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import ApplicationModal from '@galo-monitor/web-containers/modals/root/ApplicationModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const FunctionalitiesScreen = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const { data: applications, mutate, isValidating } = useGaloMonitorApi('/admin/functionalities')

  const userActions = useMemo(
    () => getActionsBySlug(routes.functionalities.slug),
    [getActionsBySlug, routes.functionalities.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'name'
      },
      {
        title: t('context'),
        field: 'context',
        defaultGroupSort: 'asc',
        defaultGroupOrder: 0
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-functionality',
        Component: ApplicationModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, application) => {
      createDialog({
        id: 'edit-application',
        Component: ApplicationModal,
        props: {
          application,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onDeleteClick = useCallback(
    (_, application) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'female',
            howMany: 1,
            this: t('permission', { howMany: 1 }),
            name: application.name
          }),
          onConfirm: () => {
            dispatch(ApplicationActions.removeApplication(
              application,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('permission', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'female', howMany: 1 }))
                }))
                mutate()
              },
              () => {
                createDialog({
                  id: 'applications-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'female',
                      action: toLower(t('remove')),
                      this: toLower(t('application', { howMany: 1 }))
                    })  }.`,
                    message: `${ t('you need detach all modules to delete a application', {
                      this: t('module', { howMany: 1 }),
                      action: toLower(t('removed', { gender: 'female', howMany: 1 }))
                    })  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const crumbs = useMemo(() => [
    {
      title: t('functionality', { howMany: 2 }),
      key: 'functionalities-screen',
      url: '/functionalities',
      active: true
    }
  ], [t])

  return (
    <Screen title={ routes.functionalities.title } breadcrumbs={ crumbs }>
      <DataTable
        loading={ isValidating }
        data={ applications || [] }
        columns={ columns }
        onAddClick={ userActions.write && onAddClick }
        onEditClick={ userActions.edit && onEditClick }
        onDeleteClick={ userActions.delete && onDeleteClick }
        options={ {
          defaultExpanded: true
        } }
      />
    </Screen>
  )
}

export default FunctionalitiesScreen
